import React, { useState, useEffect } from "react";
import LogoImg from "../../assets/images/Logo.png";
import { Form, Input, Select, DatePicker, Button, Row, Col } from "antd";
import { ToastContainer } from "react-toastify";
import Swal from "sweetalert2";
import axios from "axios";
import CryptoJS from "crypto-js";
import { config } from "../../config/config";
import BVNModificationHistoryTable from "./History/BVNModificationHistory.jsx";

const SECRET_KEY = import.meta.env.VITE_APP_SECRET_KEY;

function decryptData(ciphertext) {
  if (!ciphertext) return null;
  try {
    const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_KEY);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);
    return JSON.parse(decrypted);
  } catch {
    return null;
  }
}

const MODIFICATION_COSTS = {
  name: 100,
  dob: 100,
  address: 100,
};

function BVNModification() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [modificationType, setModificationType] = useState(null);
  const [states, setStates] = useState([]);
  const [lgas, setLgas] = useState([]);
  const [modificationCosts, setModificationCosts] = useState({
    name: 100,
    dob: 100,
    address: 100,
  });
  const [priceLoading, setPriceLoading] = useState(true);

  // Extract userPhone from encrypted user in localStorage
  const userStr = localStorage.getItem("user");
  const userObj = decryptData(userStr);
  const userPhone = userObj?.phone || userObj?.phoneNumber || "";

  const onFinish = async (values) => {
    const changeType = values.modificationType;
    const currentPrice = modificationCosts[changeType] || 0;

    // Show confirmation dialog first
    const result = await Swal.fire({
      title: "Confirm BVN Modification",
      html: `
        <p class="mb-4 text-gray-500">Are you sure you want to submit this BVN modification request?</p>
        <div class="bg-slate-50 border border-slate-100 rounded-2xl p-4 space-y-2 text-left mb-4 font-mono text-sm">
          <p class="text-sm text-gray-600"><strong>Type:</strong> Change of ${changeType}</p>
          <p class="text-sm text-gray-600"><strong>BVN:</strong> ${values.bvn}</p>
          <p class="text-sm text-gray-600"><strong>NIN:</strong> ${values.nin}</p>
        </div>
        <div class="bg-sky-50 border-2 border-sky-200 rounded-2xl py-3 px-6 inline-block shadow-sm">
          <span class="text-xs font-bold text-sky-500 uppercase tracking-wider block mb-0.5">Modification Fee</span>
          <span class="text-3xl font-black text-sky-600">₦${currentPrice.toLocaleString()}</span>
        </div>
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3b82f6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, submit",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) {
      return;
    }

    setLoading(true);
    try {
      const userId = userObj?._id || userObj?.id;

      if (!userId) {
        throw new Error("User authentication failed. Please log in again.");
      }

      // Base payload with fields common to all modification types
      let payload = {
        userId,
        changeType,
        bvn: values.bvn,
        nin: values.nin,
        amount: currentPrice,
      };

      // Add fields specific to the modification type
      if (changeType === "name") {
        payload = {
          ...payload,
          firstName: values.firstName,
          lastName: values.lastName,
          middleName: values.middleName || "",
          phone: userPhone,
        };
      } else if (changeType === "dob") {
        payload = {
          ...payload,
          dob: values.dob ? values.dob.format("DD-MM-YYYY") : "",
          phone: userPhone,
        };
      } else if (changeType === "phone") {
        payload = { ...payload, phone: values.phone };
      } else if (changeType === "address") {
        payload = {
          ...payload,
          address: values.address1,
          state: values.state,
          localGovernment: values.lga,
          phone: userPhone,
        };
      }
      
      await axios.post(
        `${config.apiBaseUrl}${config.endpoints.BVNModification}`,
        payload,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );

      await Swal.fire({
        icon: "success",
        title: "Submission Successful!",
        text: "Your BVN modification request has been submitted.",
        confirmButtonColor: "#3b82f6", // sky-500
      });
      form.resetFields();
      setModificationType(null);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text: error.response?.data?.error || error.response?.data?.message || error.message || "Failed to submit request.",
        confirmButtonColor: "#3b82f6",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleModificationTypeChange = (value) => {
    setModificationType(value);
    form.resetFields(); // Reset all fields when type changes
    form.setFieldsValue({
      modificationType: value,
      userPhone: userPhone,
    });
  };

  useEffect(() => {
    const getStatesFromApi = async () => {
      try {
        let response = await fetch("https://nga-states-lga.onrender.com/fetch");
        let json = await response.json();
        setStates(json);
      } catch (error) {}
    };
    getStatesFromApi();
  }, []);

  const handleStateChange = async (value) => {
    form.setFieldsValue({ lga: undefined }); // Reset LGA
    let response = await fetch(
      `https://nga-states-lga.onrender.com/?state=${value}`
    );
    let json = await response.json();
    setLgas(json);
  };

  // Fetch BVN Modification pricing
  useEffect(() => {
    const fetchPrices = async () => {
      setPriceLoading(true);
      try {
        const response = await axios.get(
          `${config.apiBaseUrl}${config.endpoints.currentapipricing}`,
          { withCredentials: true }
        );
        
        const costs = {
          name: 100,
          dob: 100,
          phone: 15,
          address: 100,
        };

        if (Array.isArray(response.data)) {
          const namePrice = response.data.find(item => item.key === "bvn_modification_name");
          if (namePrice && namePrice.prices) costs.name = namePrice.prices.agent;

          const dobPrice = response.data.find(item => item.key === "bvn_modification_dob");
          if (dobPrice && dobPrice.prices) costs.dob = dobPrice.prices.agent;

          const addressPrice = response.data.find(item => item.key === "bvn_modification_address");
          if (addressPrice && addressPrice.prices) costs.address = addressPrice.prices.agent;

          const phonePrice = response.data.find(item => item.key === "bvn_modification_phone");
          if (phonePrice && phonePrice.prices) costs.phone = phonePrice.prices.agent;
        }

        setModificationCosts(costs);
      } catch (error) {
        
      } finally {
        setPriceLoading(false);
      }
    };

    fetchPrices();
  }, []);

  // Keep attached phone input prefilled on modificationType or phone mount
  useEffect(() => {
    if (userPhone && modificationType) {
      form.setFieldsValue({ userPhone });
    }
  }, [userPhone, modificationType, form]);

  return (
    <div className="w-full">
      <div className="rounded-2xl mb-10 bg-white p-6 shadow-lg border border-gray-100">
        <ToastContainer />
        <div className="flex items-center justify-between mb-6 border-b pb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">BVN Modification</h2>
            <p className="text-sm text-gray-500 mt-1">Modify your BVN details</p>
          </div>
        </div>
        <div className="max-w-2xl mx-auto mt-5">
          {modificationType && (
            <div className="mb-6 p-4 rounded-2xl bg-sky-50 border-2 border-sky-200 text-sky-600 text-center shadow-sm max-w-sm mx-auto animate-fadeIn">
              <span className="text-xs font-bold uppercase tracking-wider block mb-0.5">Modification Fee</span>
              <span className="font-black text-2xl">
                ₦{priceLoading ? "..." : modificationCosts[modificationType]?.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                })}
              </span>
            </div>
          )}
          <Form form={form} onFinish={onFinish} layout="vertical">
            <Form.Item
              size="large"
              name="modificationType"
              label="Select Modification Type"
              rules={[{ required: true, message: "Please select a type" }]}
            >
              <Select
                placeholder="-- Select an Option --"
                onChange={handleModificationTypeChange}
              >
                <Select.Option value="name">Change of name</Select.Option>
                <Select.Option value="dob">Change of D.o.b</Select.Option>
                <Select.Option value="address">Change of address</Select.Option>
              </Select>
            </Form.Item>

            {modificationType && (
              <>
                {/* Form fields for Name */}
                {modificationType === "name" && (
                  <>
                    <Row gutter={16}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="firstName"
                          label="New First Name"
                          rules={[{ required: true, message: "Please enter new first name" }]}
                        >
                          <Input size="large" placeholder="New First Name" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="lastName"
                          label="New Last Name"
                          rules={[{ required: true, message: "Please enter new last name" }]}
                        >
                          <Input size="large" placeholder="New Last Name" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col xs={24} sm={12}>
                        <Form.Item name="middleName" label="Middle Name">
                          <Input size="large" placeholder="Middle Name" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="nin"
                          label="NIN"
                          rules={[{ required: true, message: "Please enter NIN" }]}
                        >
                          <Input size="large" placeholder="NIN" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="bvn"
                          label="BVN"
                          rules={[{ required: true, message: "Please enter BVN" }]}
                        >
                          <Input size="large" placeholder="BVN" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item name="userPhone" label="Attached User Phone">
                          <Input size="large" disabled placeholder="Attached User Phone" />
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                )}

                {/* Form fields for D.O.B */}
                {modificationType === "dob" && (
                  <>
                    <Row gutter={16}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="bvn"
                          label="BVN"
                          rules={[{ required: true, message: "Please enter BVN" }]}
                        >
                          <Input size="large" placeholder="BVN" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="nin"
                          label="NIN"
                          rules={[{ required: true, message: "Please enter NIN" }]}
                        >
                          <Input size="large" placeholder="NIN" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="dob"
                          label="New Date of Birth"
                          rules={[{ required: true, message: "Please select new date of birth" }]}
                        >
                          <DatePicker className="w-full" size="large" placeholder="Select New DOB" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item name="userPhone" label="Attached User Phone">
                          <Input size="large" disabled placeholder="Attached User Phone" />
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                )}

                {/* Form fields for Phone */}
                {modificationType === "phone" && (
                  <>
                    <Row gutter={16}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="bvn"
                          label="BVN"
                          rules={[{ required: true, message: "Please enter BVN" }]}
                        >
                          <Input size="large" placeholder="BVN" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="nin"
                          label="NIN"
                          rules={[{ required: true, message: "Please enter NIN" }]}
                        >
                          <Input size="large" placeholder="NIN" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col xs={24} sm={24}>
                        <Form.Item
                          name="phone"
                          label="New Phone"
                          rules={[{ required: true, message: "Please enter new phone number" }]}
                        >
                          <Input size="large" placeholder="New Phone" />
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                )}

                {/* Form fields for Address */}
                {modificationType === "address" && (
                  <>
                    <Row gutter={16}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="bvn"
                          label="BVN"
                          rules={[{ required: true, message: "Please enter BVN" }]}
                        >
                          <Input size="large" placeholder="BVN" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="nin"
                          label="NIN"
                          rules={[{ required: true, message: "Please enter NIN" }]}
                        >
                          <Input size="large" placeholder="NIN" />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="state"
                          label="State"
                          rules={[{ required: true, message: "Please select state" }]}
                        >
                          <Select
                            size="large"
                            placeholder="Select State"
                            onChange={handleStateChange}
                            showSearch
                          >
                            {states.map((state) => (
                              <Select.Option key={state} value={state}>
                                {state}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          name="lga"
                          label="LGA"
                          rules={[{ required: true, message: "Please select LGA" }]}
                        >
                          <Select size="large" placeholder="Select LGA" showSearch>
                            {lgas.map((lga) => (
                              <Select.Option key={lga} value={lga}>
                                {lga}
                              </Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col xs={24} sm={24}>
                        <Form.Item
                          name="address1"
                          label="Address"
                          rules={[{ required: true, message: "Please enter address" }]}
                        >
                          <Input size="large" placeholder="Address" />
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                )}

                <Form.Item>
                  <Button
                    type="primary"
                    size="large"
                    htmlType="submit"
                    className="w-full bg-sky-500 hover:bg-sky-600 mt-4"
                    loading={loading}
                  >
                    Submit Modification Request {priceLoading ? "" : `(₦${modificationCosts[modificationType]?.toLocaleString()})`}
                  </Button>
                </Form.Item>
              </>
            )}
          </Form>
        </div>
      </div>
      
      {/* History Table */}
      <div className="w-full mt-10">
        <BVNModificationHistoryTable />
      </div>
    </div>
  );
}

export default BVNModification;
